import { useCallback, useMemo } from 'react';

import { useSelector } from '@xstate/react';
import { type ActorRefFrom } from 'xstate';

import { type Allergen } from '@sg/graphql-schema';

import { type dietaryPreferencesMachine } from '../dietary-preferences.machine';
import {
  checkIfHasSelectedPreferences,
  checkIfHasUnsavedPreferences,
  sortPreferencesByName,
} from '../helpers';

// ─────────────────────────────────────────────────────────────────────────────

export const useDietaryPreferencesMachine = (params: Params) => {
  const { actor, onTogglePreference } = params;

  // ─── Context ─────────────────────────────────────────────────────────

  const preferences = useSelector(actor, (snapshot) => snapshot.context.preferences);

  const pendingPreferences = useSelector(actor, (snapshot) => snapshot.context.pendingPreferences);

  const sortedPreferencesNames = useMemo(() => {
    return sortPreferencesByName(pendingPreferences);
  }, [pendingPreferences]);

  // ─── Flags ───────────────────────────────────────────────────────────

  const isIdle = useSelector(actor, (snapshot) => snapshot.matches('idle'));
  const isActive = useSelector(
    actor,
    (snapshot) => snapshot.matches('active') || snapshot.matches('updatingRemotePreferences'),
  );
  const isUpdatingRemotePreferences = useSelector(actor, (snapshot) =>
    snapshot.matches('updatingRemotePreferences'),
  );
  const isFetchingCustomerPreferences = useSelector(actor, (snapshot) =>
    snapshot.matches('fetchingCustomerPreferences'),
  );
  const hasUnsavedPreferences = checkIfHasUnsavedPreferences(preferences, pendingPreferences); // prettier-ignore
  const hasSelectedPreferences = checkIfHasSelectedPreferences(preferences);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const start = useCallback(
    (startParams?: StartParams) => {
      actor.send({ type: 'START', stayActiveAfterConfirm: startParams?.stayActiveAfterConfirm });
    },
    [actor],
  );

  const togglePreference = useCallback(
    (value: Allergen) => {
      actor.send({ type: 'TOGGLE_PREFERENCE', value });
      onTogglePreference?.(value);
    },
    [actor, onTogglePreference],
  );

  const confirmChanges = useCallback(() => {
    actor.send({ type: 'CONFIRM' });
  }, [actor]);

  const cancelChanges = useCallback(() => {
    actor.send({ type: 'CANCEL' });
  }, [actor]);

  const resetPreferences = useCallback(() => {
    actor.send({ type: 'RESET' });
  }, [actor]);

  const fetchCustomerPreferences = useCallback(() => {
    actor.send({ type: 'FETCH_CUSTOMER_PREFERENCES' });
  }, [actor]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    preferences,
    pendingPreferences,
    sortedPreferencesNames,
    isIdle,
    isActive,
    isUpdatingRemotePreferences,
    isFetchingCustomerPreferences,
    hasUnsavedPreferences,
    hasSelectedPreferences,
    start,
    togglePreference,
    confirmChanges,
    cancelChanges,
    resetPreferences,
    fetchCustomerPreferences,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  actor: ActorRefFrom<typeof dietaryPreferencesMachine>;
  onTogglePreference?: (value: Allergen) => void;
};

type StartParams = {
  stayActiveAfterConfirm?: boolean;
};
